import { FormInputNumber, FormInputNumberProps } from '@systemeio/ui'
import { useTranslation } from 'react-i18next'
import { useState, useRef, useCallback } from 'react'
import { ChevronUpIcon } from '../../icons/chevron-up-icon'
import { ChevronDownIcon } from '../../icons/chevron-down-icon'
import { twMerge } from 'tailwind-merge'

type NumberFieldProps = {
  value?: number
  update: (value: number) => void
  label?: string
} & Omit<FormInputNumberProps, 'value' | 'onChange' | 'type'>

function NumberField({ value = 0, update, label, min, max, ...props }: NumberFieldProps) {
  const { t } = useTranslation()
  const [isFocused, setIsFocused] = useState(false)
  const intervalRef = useRef<number | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const currentValueRef = useRef(value)

  // Keep currentValueRef in sync with value
  currentValueRef.current = value

  const clampValue = useCallback(
    (val: number) => {
      let clamped = val
      if (max !== undefined) clamped = Math.min(clamped, max)
      if (min !== undefined) clamped = Math.max(clamped, min)
      return clamped
    },
    [min, max],
  )

  const startRepeat = useCallback(
    (step: number) => {
      const newValue = clampValue(currentValueRef.current + step)
      if (newValue === currentValueRef.current) return // Already at limit

      update(newValue)
      timeoutRef.current = window.setTimeout(() => {
        intervalRef.current = window.setInterval(() => {
          const nextValue = clampValue(currentValueRef.current + step)
          if (nextValue === currentValueRef.current) {
            // Reached limit, stop interval
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
            return
          }
          currentValueRef.current = nextValue
          update(nextValue)
        }, 100)
      }, 500)
    },
    [clampValue, update],
  )

  const stopChange = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const startIncrement = useCallback(() => startRepeat(1), [startRepeat])
  const startDecrement = useCallback(() => startRepeat(-1), [startRepeat])

  return (
    <div>
      {label && <label>{t(label)}</label>}
      <div>
        <FormInputNumber
          toFixed={0}
          className="w-[80px]"
          onChange={update}
          value={value}
          min={min}
          max={max}
          inputClassName="border-r-0 pr-0 sm:pr-0"
          addonAfterClassName={twMerge(
            `bg-transparent px-0 sm:px-0 pr-2 sm:pr-4 py-0 border-l-0 main-transition-colors`,
            isFocused
              ? 'border-y-brand-primary border-r-brand-primary'
              : 'border-y-text-secondary/30 border-r-text-secondary/30',
          )}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          addonAfter={
            <div className="flex flex-col items-center">
              <button
                tabIndex={-1}
                className="outline-none"
                onMouseDown={startIncrement}
                onMouseUp={stopChange}
                onMouseLeave={stopChange}
              >
                <ChevronUpIcon className="h-4 w-4" />
              </button>
              <button
                tabIndex={-1}
                className="outline-none"
                onMouseDown={startDecrement}
                onMouseUp={stopChange}
                onMouseLeave={stopChange}
              >
                <ChevronDownIcon className="h-4 w-4" />
              </button>
            </div>
          }
          {...props}
        />
      </div>
    </div>
  )
}

export default NumberField
